//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import CountriesModel from './countries.model';

/**
 * @service CountriesAPIService
 * @description Service for managing country reference data operations.
 * Provides HTTP methods for retrieving country information from the backend API.
 * Used to populate country dropdowns and validate location data across the application.
 * Extends base Service class with generic type CountriesModel.
 *
 * @feature 1. Fetch comprehensive list of countries
 * @feature 2. Manage HTTP request lifecycle (cancellation)
 * @feature 3. Promise-based async data retrieval
 *
 * @architecture
 * Extends base Service<CountriesModel> providing REST API integration with
 * trading community backend. Handles country reference data which typically includes
 * country names, codes, dialing codes, and other metadata. Implements request
 * tracking for cleanup and cancellation to prevent memory leaks.
 *
 * @service-endpoint /tradingcommunity/countryinfo/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CountriesAPIService extends Service<CountriesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path (/tradingcommunity/countryinfo/).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/countryinfo/');
  }

  /**
   * @method getAllCountriesList
   * @async
   * @description Fetch all country records from the backend
   *
   * Makes HTTP GET request to backend endpoint to retrieve list of countries.
   * Wraps response in Promise for async/await compatibility.
   *
   * @returns {Promise<Array<CountriesModel>>} Promise resolving to array of CountriesModel objects
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const countries = await countriesService.getAllCountriesList();
   * console.log(countries); // Array of country objects
   */
  public getAllCountriesList() {
    return new Promise<Array<CountriesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CountriesModel>>(`${this.baseUrl}${this.serviceUrl}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
